import React, {useState, useEffect} from 'react'
import {useParams, useNavigate} from 'react-router-dom'
import {useXpertKnowledgeContext} from '../XpertKnowledge/XpertKnowledgeContext'
import {Col, Block, Row} from 'jsxstyle'
import {ReactComponent as DeleteIcon} from '../../common/assets/delete-icon.svg'
import Loader from '../../components/Loader'
import {ReactComponent as LeftArrowIcon} from '../../common/assets/left-arrow-icon.svg'
import i18n from '../../common/i18n'
import {XpertKnowledgeTutorialService} from '../../services/XpertKnowledgeTutorial/XpertKnowledgeTutorial'
import {XpertKnowledgeTutorialDebugDto} from '../../services/XpertKnowledgeTutorial/type'
import {useXpertKnowledgeAuthContext} from '../../components/AuthContext/XpertKnowledgeAuthContext'
import {UserRole} from '../../services/User/type'

const XpertKnowledgeTutorialDebug: React.FC = () => {
  const {xpertKnowledgeId} = useParams<{xpertKnowledgeId: string}>()
  const {selectedXpertKnowledge, fetchXpertKnowledgeById} = useXpertKnowledgeContext()
  const {user} = useXpertKnowledgeAuthContext()
  const navigate = useNavigate()

  const [debugData, setDebugData] = useState<XpertKnowledgeTutorialDebugDto | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (xpertKnowledgeId) {
      fetchXpertKnowledgeById(xpertKnowledgeId).catch((error) => {
        console.error('Error fetching xpert knowledge:', error)
        setError('Failed to load xpert knowledge')
      })
    }
  }, [xpertKnowledgeId, fetchXpertKnowledgeById])

  useEffect(() => {
    const fetchDebugData = async () => {
      if (!selectedXpertKnowledge || !selectedXpertKnowledge.publishTutorial) return

      try {
        setIsLoading(true)
        const data = await XpertKnowledgeTutorialService.debug(selectedXpertKnowledge.id, selectedXpertKnowledge.publishTutorial.id)
        setDebugData(data)
      } catch (error) {
        console.error('Error fetching debug data:', error)
        setError('Failed to load debug data')
      } finally {
        setIsLoading(false)
      }
    }

    fetchDebugData()
  }, [selectedXpertKnowledge])

  if (user && user.role !== UserRole.ADMIN_SIGHTCALL) {
    return (
      <Col height="100vh" justifyContent="center" alignItems="center" gap="16px" backgroundColor="#f8d7da" padding="20px">
        <Block>
          <DeleteIcon width="80px" height="80px" fill="#721c24" />
        </Block>
        <Block fontSize="32px" fontWeight="bold" color="#721c24">
          {i18n.t('xk.tutorial.buffer.unauthorized')}
        </Block>
      </Col>
    )
  }

  if (isLoading) {
    return (
      <Col width="100vw" height="100vh" display="flex" justifyContent="center" alignItems="center">
        <Col gap="10px" justifyContent="center" alignItems="center" textAlign="center">
          <Loader size={75} />
          <Block fontWeight={500} lineHeight="60px" fontSize="34px">
            {i18n.t('xk.tutorial.loading')}
          </Block>
        </Col>
      </Col>
    )
  }

  if (error) {
    return (
      <Col height="100vh" justifyContent="center" alignItems="center" gap="16px" backgroundColor="#f8d7da" padding="20px">
        <Block>
          <DeleteIcon width="80px" height="80px" fill="#721c24" />
        </Block>
        <Block fontSize="24px" fontWeight="bold" color="#721c24">
          {error}
        </Block>
      </Col>
    )
  }

  const renderJsonData = (data: any, title: string) => (
    <Col gap="10px" marginBottom="30px">
      <Block fontSize="20px" fontWeight={600} color="#333" marginBottom="10px">
        {title}
      </Block>
      <Block
        backgroundColor="#f8f9fa"
        borderRadius="8px"
        padding="16px"
        border="1px solid #e9ecef"
        fontFamily="'Courier New', monospace"
        fontSize="12px"
        lineHeight="1.4"
        color="#333"
        overflowX="auto"
        whiteSpace="pre-wrap"
      >
        {JSON.stringify(data, null, 2)}
      </Block>
    </Col>
  )

  return (
    <Col padding="20px" backgroundColor="#FFFFFF" height="100vh" display="flex" flexDirection="column">
      <Row
        component="button"
        border="none"
        backgroundColor="transparent"
        padding="4px 8px 4px 3px"
        hoverBackgroundColor="#ebebed"
        gap="10px"
        cursor="pointer"
        borderRadius="4px"
        props={{
          onClick: () => navigate(`/xpert-knowledge/${xpertKnowledgeId}/tutorial`),
        }}
        alignItems="center"
        marginBottom="20px"
        width="auto"
      >
        <LeftArrowIcon width={16} height={16} color="#909090" />
        <Block color="#909090" fontWeight={500} lineHeight="24px" fontSize="16px">
          {i18n.t('xk.tutorial.back')}
        </Block>
      </Row>

      <Block fontSize="32px" fontWeight={500} lineHeight="48px" color="black" marginBottom="20px">
        Tutorial Debug Information
      </Block>

      {selectedXpertKnowledge && (
        <Block fontSize="16px" color="#666" marginBottom="20px">
          {selectedXpertKnowledge.publishTutorial?.title} - Version {selectedXpertKnowledge.publishTutorial?.version}
        </Block>
      )}

      <Col overflowY="auto" height="100%" flexGrow={1}>
        {debugData ? (
          <>
            {renderJsonData(debugData.chunks, 'Chunks')}
            {renderJsonData(debugData.gptResponse, 'GPT Response')}
          </>
        ) : (
          <Col justifyContent="center" alignItems="center" gap="20px" padding="50px">
            <Block fontSize="24px" fontWeight={500} color="#666">
              No debug data available
            </Block>
          </Col>
        )}
      </Col>
    </Col>
  )
}

export default XpertKnowledgeTutorialDebug
